import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { Capacitor } from '@capacitor/core'
import { isAxiosError } from 'axios'
import { identityClient, jsonapiClient } from '@/api/client'
import { secureStore, AUTH_JWT_KEY, AUTH_REFRESH_KEY } from '@/lib/secureStore'
import type { components } from '@/api/schema'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler'
import { useOrganizationStore } from './organization'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'

type UserResource = components['schemas']['UserResource']
type MembershipResource = components['schemas']['MembershipResource']
export type LoginResult = { ok: true } | { ok: false; message: string }

export const useAuthStore = defineStore('auth', () => {
  const { locale: i18nLocale, t } = useI18n()
  const { handleError, messageOf } = useApiErrorHandler()

  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const user = ref<UserResource | null>(null)
  const memberships = ref<MembershipResource[]>([])
  const loading = ref(false)
  const isBooted = ref(false)
  const orgStore = useOrganizationStore()

  const isAuthenticated = computed(() => !!token.value)

  // Guaranteed-present profile for protected views. Boot is blocking — the router
  // guard awaits fetchMe() before any auth-required route renders — so this is
  // always populated when a protected component reads it. Throws (rather than
  // returning null) if that invariant is ever violated, turning a silent
  // wrong-data bug into a loud, traceable one. Returns a non-null UserResource,
  // so callers skip null-guards. Use `user` directly only where absence is valid
  // (e.g. guest screens, the boot window itself).
  const currentUser = computed<UserResource>(() => {
    if (!user.value) throw new Error('auth.currentUser accessed before the profile was loaded')
    return user.value
  })

  // Persist both the access JWT and the rotating refresh token in OS-backed
  // secure storage. Storing the JWT too lets an offline cold-launch render the
  // app shell from the in-memory token before any network call. No-op on web.
  const persistSession = async () => {
    if (token.value) await secureStore.set(AUTH_JWT_KEY, token.value)
    else await secureStore.remove(AUTH_JWT_KEY)

    if (refreshToken.value) await secureStore.set(AUTH_REFRESH_KEY, refreshToken.value)
    else await secureStore.remove(AUTH_REFRESH_KEY)
  }

  const hydrateFromStorage = async () => {
    if (!Capacitor.isNativePlatform()) return
    token.value = await secureStore.get(AUTH_JWT_KEY)
    refreshToken.value = await secureStore.get(AUTH_REFRESH_KEY)
  }

  const setUser = (resource: UserResource | null) => {
    user.value = resource
    
    const locale = resource?.attributes.locale
    if (locale) {
      i18nLocale.value = locale
      localStorage.setItem('locale', locale)
    }
  }

  const fetchCurrentUser = async () => {
    loading.value = true
    try {
      const { data } = await jsonapiClient.get('/api/me')
      
      setUser(data.data)
      
      if (data.included) {
        memberships.value = data.included.filter((i): i is MembershipResource => i.type === 'membership')
      }

      isBooted.value = true
      return data.data
    } catch {
      return null
    } finally {
      loading.value = false
    }
  }

  // A failed sign-in RETURNS its message instead of toasting it: the login form
  // shows it in place and keeps it there, where the person is looking.
  const login = async (email: string, password: string, rememberMe = false): Promise<LoginResult> => {
    loading.value = true
    try {
      const { data } = await identityClient.post('/identity/login', {
        user: { email, password, remember_me: rememberMe }
      })
      token.value = data.accessToken || null
      // refreshToken is only present on native (server gates it by Origin).
      refreshToken.value = data.refreshToken ?? null
      await persistSession()

      // Fetch the full profile immediately using the new token
      await fetchCurrentUser()
      await orgStore.fetchOrganizations()
      return { ok: true }
    } catch (error) {
      return { ok: false, message: messageOf(error) }
    } finally {
      loading.value = false
    }
  }

  const tryRefreshToken = async () => {
    try {
      // Native sends the stored refresh token in the body; web relies on the
      // HttpOnly cookie (body omitted). The server rotates and returns a fresh
      // refresh token to native only.
      const body = Capacitor.isNativePlatform() && refreshToken.value
        ? { refreshToken: refreshToken.value }
        : {}
      const { data } = await identityClient.post('/identity/refresh', body)
      if (data.accessToken) {
        token.value = data.accessToken
        if (data.refreshToken) refreshToken.value = data.refreshToken
        await persistSession()
        return true
      }
      return false
    } catch (error) {
      // Only a definitive 401 (invalid/revoked token) clears the session. A
      // network error / timeout must NOT nuke a persistent session on a flaky
      // cold-launch — return false and keep the stored creds for a later retry.
      if (isAxiosError(error) && error.response?.status === 401) {
        await logout()
      }
      return false
    }
  }

  // Invalidate the session: flip `isAuthenticated` to false and clear all
  // credentials. Deliberately does NOT touch `user` / `memberships` / current
  // org — those are what protected templates read, so they're torn down later by
  // `clearProfile()` (called from the app-shell logout watcher AFTER navigation
  // has left the protected subtree). This split is what keeps `currentUser` safe
  // to use in render: the profile outlives every mounted protected component.
  const logout = async () => {
    // Best-effort server-side revocation while we still hold the refresh token.
    const stored = refreshToken.value
    token.value = null
    refreshToken.value = null
    await secureStore.clearAll()
    try {
      await identityClient.delete('/identity/logout', stored ? { data: { refreshToken: stored } } : undefined)
    } catch { /* ignore */ }
  }

  // Profile teardown — the render-sensitive half of logout. Must run only once
  // navigation has left the protected subtree (no mounted component reads `user`
  // anymore), so `currentUser` never sees null. Driven by the shell watcher.
  const clearProfile = () => {
    user.value = null
    memberships.value = []
    orgStore.setCurrentOrganization(null)
  }

  const updateProfile = async (data: object) => {
    loading.value = true
    try {
      const response = await jsonapiClient.patch('/api/me', {
        data: {
          type: 'user',
          attributes: data
        }
      })
      setUser(response.data.data)
      toast.success(t('settings.updateSuccess', 'Profile updated successfully'))
      return true
    } catch (error) {
      handleError(error)
      return false
    } finally {
      loading.value = false
    }
  }

  const currentMembership = computed(() => {
    if (!memberships.value.length || !orgStore.currentOrganizationId) return null
    
    return memberships.value.find(m => 
      m.relationships?.organization?.data?.id === orgStore.currentOrganizationId
    ) || null
  })

  return {
    token, user, currentUser, memberships, currentMembership, loading, isBooted, isAuthenticated,
    setUser, tryRefreshToken, fetchMe: fetchCurrentUser,
    login, logout, clearProfile, updateProfile, hydrateFromStorage
  }
})
